import React, { useState } from 'react';
import CTABlock from './CTABlock';

/**
 * Mini Tree - Section 02 Interactive Decision Tree
 * 
 * A 5-gate introductory tree that follows the DecisionTreeEngine visual format.
 * Uses the same connector line structure: line segment + label + line segment.
 */

// Diamond SVG component
const Diamond = ({ color = '#0057FF', size = 'sm' }) => {
  const sizes = {
    xs: { width: 6, height: 10 },
    sm: { width: 8, height: 13 },
    md: { width: 10, height: 16 },
    lg: { width: 11, height: 18 }
  };
  const { width, height } = sizes[size] || sizes.sm;
  
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 10 16" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <polygon points="5,0 10,8 5,16 0,8" fill={color}/>
    </svg>
  );
};

const gates = [
  {
    id: "01",
    type: "YES/NO",
    question: "Do you make important decisions?",
    noText: "If you don't make important decisions, you might not need a decision system yet."
  },
  {
    id: "02",
    type: "YES/NO",
    question: "Do bad ones cost you time, money, or energy?",
    noText: "If bad decisions don't cost you, you might be doing fine on instinct alone."
  },
  {
    id: "03",
    type: "YES/NO",
    question: "Do you want to make better decisions?",
    noText: "If you're not looking to improve your decisions, this system isn't for you right now."
  },
  {
    id: "04",
    type: "YES/NO",
    question: "Do you want help making better decisions?",
    noText: "If you prefer to figure it out alone, that's valid. The system is here when you're ready."
  },
  {
    id: "05",
    type: "YES/NO",
    question: "Does every major area of your life have rules that work under pressure?",
    yesText: "You already have systems that work under pressure. Binary Game can help you refine them or extend them to new areas.",
    noText: "Most people stop here. Most people say yes to the first four and stop at the fifth. That gap is exactly what Binary Game closes. One session. A system built around who you want to be. Written down, stored, and ready to use when it matters most."
  }
];

export const InteractiveTree = ({ className = '' }) => {
  const [currentGateIndex, setCurrentGateIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [exitedAtIndex, setExitedAtIndex] = useState(null);

  const isComplete = currentGateIndex >= gates.length && exitedAtIndex === null;
  const isExited = exitedAtIndex !== null;

  const handleAnswer = (gateIndex, answer) => {
    if (gateIndex !== currentGateIndex) return;
    
    const gate = gates[gateIndex];
    setAnswers(prev => ({ ...prev, [gate.id]: answer }));

    // Gates 1-4: NO exits, YES continues
    // Gate 5: Both answers show result (no exit)
    if (answer === 'no' && gateIndex < 4) {
      setExitedAtIndex(gateIndex);
    } else {
      setCurrentGateIndex(prev => prev + 1);
    }
  };

  const reset = () => {
    setCurrentGateIndex(0);
    setAnswers({});
    setExitedAtIndex(null);
  };

  const renderGate = (gate, index) => {
    const maxVisibleIndex = exitedAtIndex !== null ? exitedAtIndex : currentGateIndex;
    if (index > maxVisibleIndex) return null;

    const isActive = index === currentGateIndex && exitedAtIndex === null;
    const isResolved = index < currentGateIndex || (exitedAtIndex !== null && index <= exitedAtIndex);
    const isExitPoint = exitedAtIndex === index;
    const chosenAnswer = answers[gate.id];
    const isLastGate = index === gates.length - 1;

    return (
      <div key={gate.id} className="mt-gate-section mt-fade-in">
        {/* Gate Card */}
        <div 
          className="mt-gate-card"
          style={isResolved ? { pointerEvents: 'none' } : {}}
        >
          <div className="mt-gate-spine" />
          <div className="mt-gate-content">
            <span className="mt-gate-label">
              GATE {gate.id} | {gate.type}
            </span>
            <p className="mt-gate-question">{gate.question}</p>
            
            {/* Buttons */}
            <div className="mt-gate-buttons">
              <button
                className={`mt-btn mt-btn-yes ${isResolved && chosenAnswer !== 'yes' ? 'mt-btn-dim' : ''}`}
                onClick={() => handleAnswer(index, 'yes')}
                disabled={isResolved}
              >
                <Diamond color="#FFFFFF" size="xs" />
                YES
              </button>
              <button
                className={`mt-btn mt-btn-no ${isResolved && chosenAnswer !== 'no' ? 'mt-btn-dim' : ''}`}
                onClick={() => handleAnswer(index, 'no')}
                disabled={isResolved}
              >
                NO
              </button>
            </div>
          </div>
        </div>

        {/* Connector to next gate */}
        {isResolved && !isExitPoint && !isLastGate && (
          <div className="mt-connector mt-fade-in">
            <div className="mt-line-down" />
            <span className="mt-connector-label">{chosenAnswer === 'yes' ? 'YES' : 'NO'}</span>
            <div className="mt-line-down" />
          </div>
        )}
      </div>
    );
  };

  const renderResult = () => {
    const lastAnswer = answers["05"];
    const resultText = lastAnswer === 'yes' 
      ? gates[4].yesText 
      : gates[4].noText;

    return (
      <div className="mt-result-section mt-fade-in">
        <div className="mt-connector">
          <div className="mt-line-down" />
          <span className="mt-connector-label">{lastAnswer === 'yes' ? 'YES' : 'NO'}</span>
          <div className="mt-line-down mt-line-blue" />
        </div>
        <div className="mt-result">
          <Diamond color="#0057FF" size="lg" />
          <p className="mt-result-text">{resultText}</p>
        </div>
        
        <p className="mt-tagline">
          That was five questions. Imagine what a full session builds.
        </p>
        
        <div className="mt-cta-wrapper mt-fade-in">
          <CTABlock />
        </div>
        
        <button className="mt-reset mt-fade-in" onClick={reset}>
          RUN AGAIN
        </button>
      </div>
    );
  };

  const renderExitExplainer = () => {
    if (exitedAtIndex === null) return null;
    const gate = gates[exitedAtIndex];
    
    return (
      <div className="mt-exit-explainer mt-fade-in">
        <div className="mt-connector">
          <div className="mt-line-down" />
          <span className="mt-connector-label">NO</span>
          <div className="mt-line-down" />
        </div>
        <div className="mt-explainer-card">
          <p className="mt-explainer-text">{gate.noText}</p>
        </div>
        <button className="mt-reset mt-fade-in" onClick={reset}>
          RUN AGAIN
        </button>
      </div>
    );
  };

  return (
    <div className={`mt-tree ${className}`} data-testid="interactive-tree">
      {/* Tree Canvas - contains all tree elements */}
      <div className="mt-canvas">
        {/* START Node */}
        <div className="mt-start">START</div>
        <div className="mt-line-down" />

        {/* Gates */}
        {gates.map((gate, index) => renderGate(gate, index))}

        {/* Result (completed tree) */}
        {isComplete && renderResult()}

        {/* Exit explainer (exited early) */}
        {isExited && renderExitExplainer()}
      </div>
    </div>
  );
};

export default InteractiveTree;
